import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { initializeAnalytics, disableAnalytics } from '../lib/analytics';

const CONSENT_KEY = 'marmo_privacy_consent';

type ConsentStatus = 'pending' | 'accepted' | 'rejected';

interface PrivacyConsentContextType {
  consentStatus: ConsentStatus;
  acceptPrivacyPolicy: () => void;
  rejectPrivacyPolicy: () => void;
  resetConsent: () => void;
}

const PrivacyConsentContext = createContext<PrivacyConsentContextType | null>(null);

function getStoredConsent(): ConsentStatus {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored === 'accepted' || stored === 'rejected') return stored;
  } catch {
    // localStorage might be unavailable
  }
  return 'pending';
}

export function PrivacyConsentProvider({ children }: { children: ReactNode }) {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>(getStoredConsent);

  // Initialize or disable analytics based on consent
  useEffect(() => {
    if (consentStatus === 'accepted') {
      initializeAnalytics();
    } else {
      disableAnalytics();
    }
  }, [consentStatus]);

  const acceptPrivacyPolicy = useCallback(() => {
    setConsentStatus('accepted');
    try {
      localStorage.setItem(CONSENT_KEY, 'accepted');
    } catch {
      // ignore
    }
  }, []);

  const rejectPrivacyPolicy = useCallback(() => {
    setConsentStatus('rejected');
    try {
      localStorage.setItem(CONSENT_KEY, 'rejected');
    } catch {
      // ignore
    }
  }, []);

  const resetConsent = useCallback(() => {
    setConsentStatus('pending');
    try {
      localStorage.removeItem(CONSENT_KEY);
    } catch {
      // ignore
    }
    disableAnalytics();
  }, []);

  return (
    <PrivacyConsentContext.Provider
      value={{ consentStatus, acceptPrivacyPolicy, rejectPrivacyPolicy, resetConsent }}
    >
      {children}
    </PrivacyConsentContext.Provider>
  );
}

export function usePrivacyConsent() {
  const ctx = useContext(PrivacyConsentContext);
  if (!ctx) {
    throw new Error('usePrivacyConsent must be used within a PrivacyConsentProvider');
  }
  return ctx;
}
